import { BottomSheet, Pill, Text, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { isPast } from "date-fns";
import { useMemo, useState } from "react";

import { LoadingButton } from "../../components/LoadingButton";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { BidAmountDisplay } from "./BidAmountDisplay";
import { AuctionBottomSheetFooter } from "./Bids/AuctionBidBottomSheetFooter";
import { AuctionBottomSheetHeader } from "./Bids/AuctionBidBottomSheetHeader";
import { AuctionCountdownTimer, useAuctionTimer } from "./Bids/AuctionBidCountdownTimer";
import { AuctionShiftInfo } from "./Bids/AuctionShiftInfo";
import { PaySlider } from "./PaySlider";

interface PlaceBidBottomSheetProps {
  modalState: UseModalState;
  workplaceName: string;
  shiftStart: Date;
  shiftEnd: Date;
  timezone: string;
  auctionEndTime: Date;
  initialSliderValue: number;
  scheduledShiftDurationInHours: number;
  breakDurationInMinutes: number;
  requiresBreak: boolean;
  remainingWorkDurationInHours: number;
  minutesElapsed: number;
  auctionBidAmount?: number | undefined;
  minimumPay: number;
  maximumPay: number;
  averageWinningBid: number | undefined;
  averageTotalBidders: number | undefined;
  isLoading?: boolean;
  isInstantPay: boolean;
  instantBookPayInUsd?: number | undefined;
  isInstantBookLoading?: boolean;
  urgentPillLabel?: string;
  onPlaceBid: (pay: number) => void;
  onInstantBook?: () => void;
  onBack: () => void;
}

export function PlaceBidBottomSheet(props: PlaceBidBottomSheetProps) {
  const {
    modalState,
    workplaceName,
    shiftStart,
    shiftEnd,
    timezone,
    auctionEndTime,
    initialSliderValue,
    auctionBidAmount,
    minimumPay,
    maximumPay,
    averageWinningBid,
    averageTotalBidders,
    scheduledShiftDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
    remainingWorkDurationInHours,
    minutesElapsed,
    isLoading = false,
    isInstantPay,
    instantBookPayInUsd,
    isInstantBookLoading = false,
    urgentPillLabel,
    onPlaceBid,
    onInstantBook,
    onBack,
  } = props;

  const [pay, setPay] = useState<number>(initialSliderValue);
  const { isExpired, secondsLeft } = useAuctionTimer(auctionEndTime);

  const dateDisplay = useMemo(
    () => formatDayOfMonthWithDayName(shiftStart, { timezone }),
    [shiftStart, timezone]
  );

  const timeDisplay = useMemo(
    () =>
      formatTimeRange(
        { startDate: shiftStart, endDate: shiftEnd },
        { timezone, useNowForStartDate: isPast(shiftStart) }
      ),
    [shiftStart, shiftEnd, timezone]
  );

  return (
    <BottomSheet modalState={modalState}>
      <DialogContent sx={{ backgroundColor: "unset", py: 8, mt: 8 }}>
        <AuctionBottomSheetHeader title="Place your bid" onBack={onBack} />

        <Stack spacing={6} sx={{ width: "100%" }}>
          <AuctionShiftInfo
            workplaceName={workplaceName}
            dateDisplay={dateDisplay}
            timeDisplay={timeDisplay}
          />

          {urgentPillLabel && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Pill
                size="small"
                variant="filled"
                label={urgentPillLabel}
                iconType="clock-filled"
                backgroundColor={(theme) => theme.palette.shift?.urgent.background}
                color={(theme) => theme.palette.shift?.urgent.text}
                iconColor={(theme) => theme.palette.shift?.urgent.icon}
                sx={{ width: "fit-content" }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <AuctionCountdownTimer secondsLeft={secondsLeft} />
          </Box>

          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 4,
              py: 8,
              width: "100%",
              padding: "1rem",
            }}
          >
            <Stack spacing={6} alignItems="center">
              <BidAmountDisplay bidAmount={pay} isExpired={isExpired} />
              {averageWinningBid && (
                <Text
                  bold
                  variant="body2"
                  color={(theme) => theme.palette.text.secondary}
                  sx={{ textAlign: "center" }}
                >
                  Average Winning Bid: {formatDollarsAsUsd(averageWinningBid)}/hr
                </Text>
              )}
              <Stack width="100%">
                <PaySlider
                  range={{ minimumRate: minimumPay, maximumRate: maximumPay }}
                  averageWinningBid={averageWinningBid}
                  value={pay}
                  disabled={isExpired}
                  sx={() => ({
                    boxSizing: "border-box",
                  })}
                  railBackgroundColor={(theme) => theme.palette.background.invert}
                  onChange={(value) => {
                    if (value) {
                      setPay(value);
                    }
                  }}
                />
                {averageTotalBidders && (
                  <Text
                    bold
                    variant="body2"
                    color={(theme) => theme.palette.text.secondary}
                    sx={{ textAlign: "center" }}
                  >
                    Every Auction has ~{averageTotalBidders} other participants.
                  </Text>
                )}
              </Stack>
            </Stack>
          </Box>

          {isDefined(instantBookPayInUsd) && (
            <Text
              semibold
              variant="body2"
              color={(theme) => theme.palette.text.secondary}
              sx={{ textAlign: "center" }}
            >
              You can skip bidding and book instantly at {formatDollarsAsUsd(instantBookPayInUsd)}
              /hr
            </Text>
          )}
        </Stack>
      </DialogContent>

      <AuctionBottomSheetFooter
        isInstantPay={isInstantPay}
        scheduledShiftDurationInHours={scheduledShiftDurationInHours}
        breakDurationInMinutes={breakDurationInMinutes}
        requiresBreak={requiresBreak}
        remainingWorkDurationInHours={remainingWorkDurationInHours}
        minutesElapsed={minutesElapsed}
        bidAmount={pay}
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          {isDefined(instantBookPayInUsd) && (
            <LoadingButton
              fullWidth
              size="large"
              variant="outlined"
              disabled={isExpired || isDefined(auctionBidAmount)}
              isLoading={isInstantBookLoading}
              onClick={() => {
                onInstantBook?.();
              }}
            >
              Book now at {formatDollarsAsUsd(instantBookPayInUsd)}/hr
            </LoadingButton>
          )}
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            disabled={!pay || isExpired || isDefined(auctionBidAmount)}
            isLoading={isLoading}
            onClick={() => {
              if (pay) {
                onPlaceBid(pay);
              }
            }}
          >
            {auctionBidAmount
              ? `Bid placed at ${formatDollarsAsUsd(auctionBidAmount)}/hr`
              : "Review bid"}
          </LoadingButton>
        </Stack>
      </AuctionBottomSheetFooter>
    </BottomSheet>
  );
}
